import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, Box, Text, Badge, Flex,
  useToast, List, ListItem, useDisclosure, Wrap, WrapItem,
  Menu, MenuButton, MenuList, MenuItem, MenuOptionGroup, MenuItemOption,
} from '@chakra-ui/react';
import { FiPlus, FiX, FiShoppingBag, FiChevronDown } from 'react-icons/fi';
import CompanyModal from './CompanyModal';
import api from '../api';

export default function SuppliersModal({ projectId, taskId, gridTasks, isOpen, onClose, onRefresh }) {
  const [suppliers, setSuppliers] = useState([]);   // [{ company_id(uuid), name, task_ids:[uuid] }]
  const [projectTasks, setProjectTasks] = useState([]); // [{ id(uuid), title }]
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [open, setOpen] = useState(false);
  const debounce = useRef(null);
  const toast = useToast();
  const companyModal = useDisclosure();
  const [pendingName, setPendingName] = useState('');

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    try {
      const [supRes, taskRes] = await Promise.all([
        api.get(`/projects/${projectId}/suppliers`).catch(() => ({ data: [] })),
        api.get(`/tasks?project_id=${encodeURIComponent(projectId)}`).catch(() => ({ data: [] })),
      ]);
      setSuppliers(Array.isArray(supRes.data) ? supRes.data.map(s => ({ ...s, task_ids: s.task_ids || [] })) : []);
      // Prefer the tasks already loaded in the grid (always present, UUID-keyed,
      // and guaranteed to match what the user sees) — fall back to the /tasks
      // fetch only when the modal wasn't given them.
      const fetched = Array.isArray(taskRes.data) ? taskRes.data : [];
      const source = (Array.isArray(gridTasks) && gridTasks.length) ? gridTasks : fetched;
      setProjectTasks(source.map(t => ({ id: t.id, title: t.task_title || t.title || t.name || 'Untitled task' })));
    } catch(e) { console.error(e); }
  }, [projectId, gridTasks]);

  useEffect(() => { if (isOpen && projectId) fetchData(); }, [isOpen, projectId, fetchData]);

  const search = async (q) => {
    if (q.length < 1) { setResults([]); setOpen(false); return; }
    try {
      const { data } = await api.get(`/companies/search/suppliers?q=${encodeURIComponent(q)}`);
      setResults((data || []).filter(c => !suppliers.find(s => String(s.company_id) === String(c.id))));
      setOpen(true);
    } catch(e) {}
  };

  // Set which tasks a supplier is assigned to (task_ids are UUIDs). Empty = none.
  const saveSupplierTasks = async (companyId, taskIds) => {
    const prev = suppliers;
    setSuppliers(s => s.map(x => String(x.company_id) === String(companyId) ? { ...x, task_ids: taskIds } : x)); // optimistic
    try {
      await api.put(`/projects/${projectId}/supplier-tasks`, { company_id: companyId, task_ids: taskIds });
      if (onRefresh) onRefresh();
    } catch(e) {
      setSuppliers(prev);
      toast({ title: 'Could not update task assignment', status: 'error', duration: 2000 });
    }
  };

  const handleAdd = async (company) => {
    try {
      // Adds the company as a project supplier; pre-assign the task the modal was opened from
      await api.put(`/projects/${projectId}/supplier-tasks`, {
        company_id: company.id,
        task_ids: taskId ? [taskId] : [],
      });
      await fetchData();
      if (onRefresh) onRefresh();
      setQuery(''); setOpen(false); setShowSearch(false);
      toast({ title: `${company.name} added as supplier`, status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Failed to add', status: 'error', duration: 2000 }); }
  };

  const handleRemove = async (companyId) => {
    const newIds = suppliers.filter(s => String(s.company_id) !== String(companyId)).map(s => s.company_id);
    try {
      await api.put(`/projects/${projectId}/suppliers`, { company_ids: newIds });
      await fetchData();
      if (onRefresh) onRefresh();
      toast({ title: 'Supplier removed', status: 'info', duration: 1500 });
    } catch(e) { toast({ title: 'Failed to remove', status: 'error', duration: 2000 }); }
  };

  const handleCompanyCreated = (company) => { handleAdd(company); };

  const titleFor = (uuid) => (projectTasks.find(t => String(t.id) === String(uuid)) || {}).title;

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">
          <Flex align="center" gap={2}><FiShoppingBag /> Suppliers</Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="11px" color="gray.500" mb={3}>
            Add a supplier, then assign it to any tasks in this project. The supplier icon and count
            light up on each task it is assigned to.
          </Text>

          {suppliers.length === 0 ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>No suppliers added yet.</Text>
          ) : (
            <VStack spacing={2} align="stretch" mb={4}>
              {suppliers.map(s => (
                <Box key={s.company_id} p={3} border="1px" borderColor="gray.100" borderRadius="md">
                  <Flex align="center" justify="space-between" gap={2}>
                    <Text fontSize="13px" fontWeight="600" flex={1} noOfLines={1}>{s.name}</Text>
                    <Menu closeOnSelect={false} isLazy>
                      <MenuButton as={Button} size="xs" variant="outline" rightIcon={<FiChevronDown />} minW="150px" textAlign="left" fontWeight="500">
                        {s.task_ids.length ? `${s.task_ids.length} task${s.task_ids.length > 1 ? 's' : ''}` : 'Assign to tasks…'}
                      </MenuButton>
                      <MenuList maxH="280px" overflowY="auto" zIndex={2000}>
                        {projectTasks.length === 0 ? (
                          <MenuItem isDisabled fontSize="xs">No tasks in this project</MenuItem>
                        ) : (
                          <MenuOptionGroup type="checkbox" value={s.task_ids}
                            onChange={(vals) => saveSupplierTasks(s.company_id, Array.isArray(vals) ? vals : [vals])}>
                            {projectTasks.map(t => (
                              <MenuItemOption key={t.id} value={t.id} fontSize="xs">{t.title}</MenuItemOption>
                            ))}
                          </MenuOptionGroup>
                        )}
                      </MenuList>
                    </Menu>
                    <Button size="xs" variant="ghost" colorScheme="red" onClick={() => handleRemove(s.company_id)} aria-label="Remove supplier">
                      <FiX />
                    </Button>
                  </Flex>
                  {s.task_ids.length > 0 && (
                    <Wrap spacing={1} mt={2}>
                      {s.task_ids.map(tid => (
                        <WrapItem key={tid}>
                          <Badge colorScheme="orange" fontSize="9px" borderRadius="full" px={2}>{titleFor(tid) || 'Task'}</Badge>
                        </WrapItem>
                      ))}
                    </Wrap>
                  )}
                </Box>
              ))}
            </VStack>
          )}

          {!showSearch ? (
            <Button size="xs" leftIcon={<FiPlus />} colorScheme="blue" variant="outline"
              onClick={() => setShowSearch(true)} w="100%">Add Supplier</Button>
          ) : (
            <Box position="relative">
              <HStack>
                <Input size="xs" value={query} autoFocus fontSize="11px" placeholder="Search company..."
                  onChange={(e) => { setQuery(e.target.value); clearTimeout(debounce.current); debounce.current = setTimeout(() => search(e.target.value), 200); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && results.length === 0 && query.trim()) { setPendingName(query.trim()); companyModal.onOpen(); }
                    if (e.key === 'Escape') { setShowSearch(false); setQuery(''); setOpen(false); }
                  }} />
                <Button size="xs" variant="ghost" onClick={() => { setShowSearch(false); setQuery(''); setOpen(false); }}>Cancel</Button>
              </HStack>
              <Text fontSize="9px" color="gray.400" mt={0.5}>Search existing or press Enter to create new</Text>
              {open && results.length > 0 && (
                <Box position="absolute" top="32px" left={0} right={0} bg="white" border="1px" borderColor="gray.200"
                  borderRadius="md" boxShadow="lg" zIndex={200} maxH="150px" overflowY="auto">
                  <List spacing={0}>
                    {results.map(c => (
                      <ListItem key={c.id} px={2} py={1} fontSize="11px" cursor="pointer" _hover={{ bg: 'blue.50' }} onClick={() => handleAdd(c)}>
                        <Text fontWeight="500">{c.name}</Text>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>

    <CompanyModal isOpen={companyModal.isOpen} onClose={companyModal.onClose} initialName={pendingName} onCreated={handleCompanyCreated} />
    </>
  );
}
